import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { signOut } from '../lib/firebase';
import { Me, type UserSummary } from '../lib/api';
import { ensureNotificationPermission } from '../lib/notifications';
import { TicketEventsProvider } from '../lib/events';
import { ToastQueue } from './ToastQueue';
import { DeploymentDialog } from './DeploymentDialog';
import { ActivityDrawer } from './ActivityDrawer';
import { DialogHost } from './ui/confirm';

export function Layout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [me, setMe] = useState<UserSummary | null>(null);
  const [deployOpen, setDeployOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  useEffect(() => {
    if (user) Me.get().then((r) => setMe(r.user)).catch(() => undefined);
  }, [user]);

  // Ask once per session for permission. The browser only shows the prompt the first time;
  // subsequent calls are no-ops if the user already granted or denied.
  useEffect(() => {
    if (user) void ensureNotificationPermission();
  }, [user]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'text-brand-600 font-medium' : 'text-slate-600 hover:text-slate-900';

  const isAdmin = me?.role === 'ADMIN';
  const isDevOrAdmin = me?.role === 'ADMIN' || me?.role === 'DEVELOPER';

  return (
    <TicketEventsProvider>
    <div className="min-h-full flex flex-col">
      <ToastQueue me={me} />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <Link to="/" className="font-semibold text-slate-900 text-sm sm:text-base">J1L Ticketing</Link>
            <nav className="hidden sm:flex gap-4 text-sm">
              <NavLink to="/" end state={{ refreshList: true }} className={linkCls}>Tickets</NavLink>
              <NavLink to="/tickets/new" className={linkCls}>New ticket</NavLink>
              {isAdmin && <NavLink to="/queue" className={linkCls}>Priority queue</NavLink>}
              {isDevOrAdmin && <NavLink to="/deployments" className={linkCls}>Deployments</NavLink>}
              {isDevOrAdmin && <NavLink to="/reports" className={linkCls}>Reports</NavLink>}
              <NavLink to="/suggestions" className={linkCls}>Suggestions</NavLink>
            </nav>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-sm">
            {isAdmin && (
              <button
                onClick={() => setDeployOpen(true)}
                className="px-2 py-1 rounded-md bg-brand-600 text-white text-xs font-medium hover:bg-brand-700"
                type="button"
              >
                Record deployment
              </button>
            )}
            <button
              onClick={() => setActivityOpen(true)}
              className="p-1.5 text-slate-500 hover:text-slate-900"
              type="button"
              aria-label="Open activity"
              title="Activity"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z" />
              </svg>
            </button>
            <span className="text-slate-600 truncate max-w-[160px]">{user?.email}</span>
            <NavLink to="/settings" className={linkCls}>Settings</NavLink>
            <button onClick={handleSignOut} className="text-slate-500 hover:text-slate-900">Sign out</button>
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="sm:hidden p-2 text-slate-600"
            aria-label="Menu"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              {menuOpen ? (
                <path fillRule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              )}
            </svg>
          </button>
        </div>
        {menuOpen && (
          <div className="sm:hidden border-t border-slate-200 bg-white">
            <div className="max-w-6xl mx-auto px-4 py-2 flex flex-col gap-2 text-sm">
              <NavLink to="/" end state={{ refreshList: true }} onClick={() => setMenuOpen(false)} className={linkCls}>Tickets</NavLink>
              <NavLink to="/tickets/new" onClick={() => setMenuOpen(false)} className={linkCls}>New ticket</NavLink>
              {isAdmin && (
                <NavLink to="/queue" onClick={() => setMenuOpen(false)} className={linkCls}>
                  Priority queue
                </NavLink>
              )}
              {isDevOrAdmin && (
                <NavLink to="/deployments" onClick={() => setMenuOpen(false)} className={linkCls}>
                  Deployments
                </NavLink>
              )}
              {isDevOrAdmin && (
                <NavLink to="/reports" onClick={() => setMenuOpen(false)} className={linkCls}>
                  Reports
                </NavLink>
              )}
              <NavLink to="/suggestions" onClick={() => setMenuOpen(false)} className={linkCls}>
                Suggestions
              </NavLink>
              <NavLink to="/settings" onClick={() => setMenuOpen(false)} className={linkCls}>
                Settings
              </NavLink>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setActivityOpen(true);
                }}
                className="text-left text-slate-700"
                type="button"
              >
                Activity
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setDeployOpen(true);
                  }}
                  className="text-left text-brand-700 font-medium"
                  type="button"
                >
                  Record deployment
                </button>
              )}
              <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
                <span className="text-slate-600 truncate">{user?.email}</span>
                <button onClick={handleSignOut} className="text-slate-500 hover:text-slate-900">Sign out</button>
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
          <Outlet />
        </div>
      </main>
      {deployOpen && (
        <DeploymentDialog
          onClose={() => setDeployOpen(false)}
          onSaved={() => setDeployOpen(false)}
        />
      )}
      <ActivityDrawer open={activityOpen} onClose={() => setActivityOpen(false)} />
      <DialogHost />
    </div>
    </TicketEventsProvider>
  );
}
